import React, { useState } from 'react';
import { X, Plus, Package, Upload } from 'lucide-react';
import { getProductImageUrl } from '../api';
import ModalShell from './ModalShell';

const ProductModal = ({ categories = [], onClose, onSubmit, editingProduct, onAddCategory }) => {
  const normalizedCategories = categories.map((cat) => {
    const name = cat.name || cat.category_name || cat.title || '';
    const id = String(cat.id ?? cat.category_id ?? cat._id ?? name);
    return { id, name };
  });

  const [formData, setFormData] = useState(() => {
    const firstCat = normalizedCategories[0];
    const editingCategoryId =
      (editingProduct?.categoryId != null
        ? String(editingProduct.categoryId)
        : editingProduct?.category_id != null
        ? String(editingProduct.category_id)
        : undefined) ||
      (editingProduct?.category &&
        normalizedCategories.find((c) => c.name === editingProduct.category)?.id);
    const initialCategoryId = editingCategoryId || firstCat?.id || '';
    const initialCategoryName =
      editingProduct?.category ||
      normalizedCategories.find((c) => c.id === initialCategoryId)?.name ||
      firstCat?.name ||
      '';

    return {
      id: editingProduct?.id,
      name: editingProduct?.name || '',
      categoryId: initialCategoryId,
      category: initialCategoryName,
      description: editingProduct?.description || '',
      price: editingProduct?.price || editingProduct?.sellingPrice || '',
      castingPrice: editingProduct?.castingPrice || editingProduct?.costingPrice || '',
      stock: editingProduct?.stock || editingProduct?.stockQuantity || '',
      stockUnit: editingProduct?.stockUnit || editingProduct?.weightUnit || 'kg',
      weight: editingProduct?.weight || '',
      expiryDate: editingProduct?.expiryDate || '',
      batchNumber: editingProduct?.batchNumber || '',
      images: [],
    };
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const existingImages = Array.isArray(editingProduct?.images) ? editingProduct.images : [];
  const existingImageCount = existingImages.length || (editingProduct?.imageUrl ? 1 : 0);
  const maxNewImages = Math.max(0, 5 - existingImageCount);
  const isEdit = !!editingProduct?.id;
  const existingImageUrls = existingImages
    .map((img) => getProductImageUrl(img.image_path || img.path))
    .filter(Boolean);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const allowed = isEdit ? Math.min(files.length, maxNewImages) : Math.min(files.length, 5);
    const toAdd = files.slice(0, allowed);
    const newImages = [...formData.images, ...toAdd].slice(0, isEdit ? maxNewImages : 5);
    setFormData({ ...formData, images: newImages });

    const newPreviews = [];
    newImages.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === newImages.length) setImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (
      !formData.name || !formData.categoryId || !formData.price || !formData.castingPrice
      || !formData.weight || !formData.stock || !formData.expiryDate || !formData.batchNumber
    ) return;

    onSubmit({
      id: formData.id,
      name: formData.name,
      categoryId: formData.categoryId,
      category: formData.category,
      description: formData.description,
      price: formData.price,
      castingPrice: formData.castingPrice,
      stock: formData.stock,
      stockUnit: formData.stockUnit,
      weight: formData.weight,
      expiryDate: formData.expiryDate,
      batchNumber: formData.batchNumber,
    }, formData.images);
  };

  return (
    <ModalShell
      title={editingProduct ? 'Edit Product' : 'Add New Product'}
      subtitle="Catalog item details and inventory"
      icon={Package}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={(
        <>
          <button type="button" onClick={handleSubmit} className="ui-btn-primary flex-1">
            {editingProduct ? 'Update Product' : 'Add Product'}
          </button>
          <button type="button" onClick={onClose} className="ui-btn-secondary flex-1">
            Cancel
          </button>
        </>
      )}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="ui-label">Product Name</label>
            <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="ui-input" placeholder="Enter product name" />
          </div>
          <div>
            <label className="ui-label">Category</label>
            <div className="flex gap-2">
              <select
                defaultValue={formData.categoryId}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = normalizedCategories.find((cat) => cat.id === value);
                  handleInputChange('categoryId', value);
                  handleInputChange('category', selected?.name || '');
                }}
                className="ui-input flex-1"
              >
                {normalizedCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button type="button" onClick={onAddCategory} className="ui-btn-outline px-3" title="Add New Category">
                <Plus size={16} />
              </button>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="ui-label">Product Description</label>
            <textarea value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="ui-input" placeholder="Enter product description" rows={3} />
          </div>
          <div>
            <label className="ui-label">Selling Price (₹)</label>
            <input type="number" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className="ui-input" placeholder="Enter price" />
          </div>
          <div>
            <label className="ui-label">Costing Price (₹)</label>
            <input type="number" value={formData.castingPrice} onChange={(e) => handleInputChange('castingPrice', e.target.value)} className="ui-input" placeholder="Enter costing price" />
          </div>
          <div>
            <label className="ui-label">Product Weight</label>
            <div className="flex gap-2">
              <input type="number" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} className="ui-input flex-1" placeholder="e.g. 500" />
              <select value={formData.stockUnit} onChange={(e) => handleInputChange('stockUnit', e.target.value)} className="ui-input w-auto">
                <option value="g">grams</option>
                <option value="kg">kilogram</option>
                <option value="pieces">pieces</option>
                <option value="liter">litres</option>
                <option value="ml">millilitres</option>
                <option value="box">box</option>
              </select>
            </div>
          </div>
          <div>
            <label className="ui-label">Stock Quantity</label>
            <input type="number" value={formData.stock} onChange={(e) => handleInputChange('stock', e.target.value)} className="ui-input" placeholder="Enter quantity" />
          </div>
          <div>
            <label className="ui-label">Expiry Date</label>
            <input type="date" value={formData.expiryDate} onChange={(e) => handleInputChange('expiryDate', e.target.value)} className="ui-input" />
          </div>
          <div>
            <label className="ui-label">Batch Number</label>
            <input type="text" value={formData.batchNumber} onChange={(e) => handleInputChange('batchNumber', e.target.value)} className="ui-input" placeholder="Enter batch number" />
          </div>
        </div>

        <div>
          <label className="ui-label">Product Images (Max 5)</label>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-2xl p-6 cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors">
            <Upload className="w-8 h-8 text-emerald-500" />
            <p className="text-sm font-semibold text-slate-600">Click to upload images</p>
            <p className="text-xs text-slate-400">PNG, JPG up to 10MB each</p>
            {isEdit && existingImageCount > 0 && (
              <p className="text-xs text-amber-600 font-medium">Existing: {existingImageCount}. You can add up to {maxNewImages} more.</p>
            )}
            <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
          </label>

          {isEdit && existingImageUrls.length > 0 && (
            <div className="mt-4">
              <p className="ui-label mb-2">Saved images</p>
              <div className="grid grid-cols-5 gap-3">
                {existingImageUrls.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                    <img src={url} alt={`Saved ${index + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {imagePreviews.length > 0 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-20 object-cover rounded-xl border border-slate-200" />
                  <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default ProductModal;
